import { type InputHTMLAttributes, forwardRef, useId } from 'react';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses: Record<NonNullable<InputProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-body',
  lg: 'px-5 py-3 text-body',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, size = 'md', className = '', id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={[
            'w-full rounded-sm border bg-bg-card text-text-primary placeholder:text-text-secondary/60',
            'transition-all duration-fast ease-fast',
            'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-primary focus:shadow-focus',
            error
              ? 'border-error ring-1 ring-error/30'
              : 'border-border hover:border-primary/40',
            'disabled:opacity-50 disabled:pointer-events-none',
            sizeClasses[size],
            className,
          ].join(' ')}
          {...props}
        />
        {error && (
          <p id={errorId} className="text-sm text-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
